import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { gsap } from 'gsap'
import { useAuth } from '@/context/AuthContext'
import { useCart } from '@/context/CartContext'
import { useUI } from '@/context/UIContext'
import Button from '@/components/UI/Button'
import Logo from '@/components/UI/Logo'
import Navbar from '@/components/Navigation/Navbar'
import MiniCart from '@/components/Cart/MiniCart'
import { ShoppingCartIcon, UserIcon, Bars3Icon } from '@heroicons/react/24/outline'

/**
 * Header - Main site header with navigation and user controls
 * 
 * @component
 */
const Header = () => {
  const { isAuthenticated, user, logout } = useAuth()
  const { itemCount } = useCart()
  const { toggleSidebar, toggleModal } = useUI()
  const navigate = useNavigate()
  
  // Refs for GSAP animations
  const headerRef = useRef(null)
  const logoRef = useRef(null)
  const navRef = useRef(null)
  const cartRef = useRef(null)
  const userRef = useRef(null)

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  // Simplified GSAP animations
  useEffect(() => {
    if (!headerRef.current) return

    const ctx = gsap.context(() => {
      // Simple fade in animation
      gsap.fromTo([logoRef.current, navRef.current, cartRef.current, userRef.current], 
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: "power2.out" }
      )
    }, headerRef)

    return () => ctx.revert()
  }, [])

  return (
    <header 
      ref={headerRef}
      className="bg-gradient-to-r from-green-600 via-green-500 to-emerald-500 shadow-2xl sticky top-0 z-50 relative overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-24 h-24 bg-yellow-300 rounded-full blur-xl animate-bounce"></div>
        <div className="absolute top-1/2 left-1/2 w-16 h-16 bg-white rounded-full blur-lg animate-spin"></div>
        </div>

      <div className="container-custom relative z-10">
        {/* Main header */}
        <div className="flex items-center justify-between py-4 sm:py-6">
          {/* Logo - Mobile optimized with PNG support */}
          <Link to="/" ref={logoRef}>
            <Logo 
              size="lg" 
              showText={true} 
              textSize="lg"
              className="hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Navigation - Hidden on mobile, shown on desktop */}
          <nav ref={navRef} className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {[
              { name: 'Home', path: '/' },
              { name: 'Products', path: '/products' },
              { name: 'Categories', path: '/categories' },
              { name: 'Contact', path: '/contact' }
            ].map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className="text-white font-semibold text-base xl:text-lg hover:text-green-200 transition-colors duration-300 relative group hover:scale-105 transform"
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white group-hover:w-full transition-all duration-300"></span>
              </Link>
            ))}
          </nav>

          {/* User actions - Mobile optimized */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* User menu */}
            {isAuthenticated ? (
              <div ref={userRef} className="relative group">
                <button className="flex items-center space-x-1 sm:space-x-2 p-2 sm:p-3 text-white hover:text-green-200 transition-colors bg-white/10 rounded-lg sm:rounded-xl backdrop-blur-sm hover:bg-white/20 hover:scale-105 transform">
                  <UserIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                  <span className="hidden sm:block font-medium text-sm sm:text-base">{user?.firstName || 'User'}</span>
                </button>
                
                {/* Dropdown menu - Hidden on mobile, shown on desktop */}
                <div className="hidden sm:block absolute right-0 top-full mt-2 w-48 sm:w-56 bg-white/95 backdrop-blur-lg rounded-xl shadow-2xl border border-green-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <div className="py-3">
                    {[
                      { name: 'Profile', path: '/profile' },
                      { name: 'Orders', path: '/orders' },
                      { name: 'Wishlist', path: '/wishlist' }
                    ].map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        className="block px-4 py-2 sm:py-3 text-gray-700 hover:bg-green-50 hover:text-green-600 transition-all duration-200 font-medium text-sm sm:text-base"
                      >
                        {item.name}
                      </Link>
                    ))}
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 sm:py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all duration-200 font-medium text-sm sm:text-base"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div ref={userRef} className="hidden sm:flex items-center space-x-2 sm:space-x-3">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-white border-white hover:bg-white hover:text-green-600 font-semibold hover:scale-105 transform transition-transform duration-300 text-xs sm:text-sm px-3 sm:px-4 py-2"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    variant="secondary" 
                    size="sm"
                    className="bg-white text-green-600 hover:bg-green-50 font-semibold shadow-lg hover:scale-105 transform transition-transform duration-300 text-xs sm:text-sm px-3 sm:px-4 py-2"
                  >
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}

            {/* Cart - Mobile optimized */}
            <button
              ref={cartRef}
              onClick={() => toggleModal('cartDrawer')}
              className="relative p-2 sm:p-3 text-white hover:text-green-200 transition-colors bg-white/10 rounded-lg sm:rounded-xl backdrop-blur-sm hover:scale-105 hover:bg-white/20 transform transition-all duration-300"
            >
              <ShoppingCartIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-yellow-400 text-green-600 text-xs rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center font-bold shadow-lg">
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => toggleSidebar('mobileMenu')}
              className="lg:hidden p-2 sm:p-3 text-white hover:text-green-200 transition-colors bg-white/10 rounded-lg sm:rounded-xl backdrop-blur-sm hover:scale-105 transform transition-transform duration-300"
            >
              <Bars3Icon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mini cart drawer */}
      <MiniCart />
    </header>
  )
}

export default Header
